import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button, ExternalLink, Separator } from '~/components';
import { AnchorTargets, SearchParamModals } from '~/constants/url';
import { useDialog } from '~/hooks/useDialog';
import { useShouldOpenModal } from '~/hooks/useShouldOpenModal';

import type { NumberingSeriesFormSection } from './NumberingSeries/types';

import { SettingsSection, SettingsSections } from '../components/SettingsSection';
import { CreateCustomConditionModal } from './CustomConditions/CreateCustomConditionModal/CreateCustomConditionModal';
import { CustomConditionsTable } from './CustomConditions/CustomConditionsTable';
import { InvoiceTheme } from './InvoiceTheme/InvoiceTheme';
import { CreateNumberingSeriesModal } from './NumberingSeries/CreateNumberingSeriesModal';
import { NumberingSeriesTable } from './NumberingSeries/NumberingSeriesTable';

export const Invoice = () => {
  const { t } = useTranslation(['settings']);

  const createNumberingSeriesDialog = useDialog(useShouldOpenModal(SearchParamModals.ADD_NUMBERING_SERIES));
  const [expandedNumberingSeries, setExpandedNumberingSeries] = useState<{ id: number; section: NumberingSeriesFormSection } | null>(null);

  const [isCustomConditionWizardOpen, setIsCustomConditionWizardOpen] = useState(false);

  return (
    <SettingsSections>
      <SettingsSection id={AnchorTargets.SETTINGS_INVOICE_THEME} title={t('settings:general.themeInvoice.title')}>
        <InvoiceTheme />
      </SettingsSection>

      <Separator />

      <SettingsSection
        action={
          <Button icon="Add" onClick={() => createNumberingSeriesDialog.open()}>
            {t('settings:general.numberingSeries.add')}
          </Button>
        }
        description={<Trans components={{ a: <ExternalLink /> }} i18nKey="settings:general.numberingSeries.description" />}
        id={AnchorTargets.SETTINGS_INVOICE_NUMBERING_SERIES}
        title={t('settings:general.numberingSeries.title')}
      >
        <NumberingSeriesTable expandedNumberingSeries={expandedNumberingSeries} setExpandedNumberingSeries={setExpandedNumberingSeries} />

        {createNumberingSeriesDialog.isOpen && (
          <CreateNumberingSeriesModal
            onClose={createNumberingSeriesDialog.close}
            onCreate={(id) => setExpandedNumberingSeries({ id, section: 'format_and_options' })}
          />
        )}
      </SettingsSection>

      <Separator />

      <SettingsSection
        action={
          <Button icon="Add" onClick={() => setIsCustomConditionWizardOpen(true)}>
            {t('settings:general.customConditions.create')}
          </Button>
        }
        description={t('settings:general.customConditions.description')}
        id={AnchorTargets.SETTINGS_INVOICE_CUSTOM_CONDITIONS}
        title={t('settings:general.customConditions.title')}
      >
        <CustomConditionsTable />

        {isCustomConditionWizardOpen && <CreateCustomConditionModal onClose={() => setIsCustomConditionWizardOpen(false)} />}
      </SettingsSection>
    </SettingsSections>
  );
};
